import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosClient  from '../utils/axiosClient';
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaUsers,
  FaEdit,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchClient();
  }, [id]);

  const fetchClient = async () => {
    try {
      setLoading(true);
      const res = await axiosClient.get(`/clients/${id}`);
      setClient(res.data);
    } catch (err) {
      console.error("❌ Error fetching client:", err);
      setError("Failed to load client data.");
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-64 text-gray-600">
        <div className="animate-spin border-4 border-gray-300 border-t-blue-500 rounded-full h-8 w-8"></div>
      </div>
    );

  if (error)
    return (
      <div className="text-center text-red-500 font-medium mt-6">{error}</div>
    );

  if (!client)
    return (
      <div className="text-center text-gray-500 mt-6">
        Client not found or removed.
      </div>
    );

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-semibold text-gray-800 tracking-tight">
          {client.agency || "Unnamed Client"}
        </h1>
        <button
          onClick={() => navigate(`/clients/edit/${client.id}`)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg shadow-md transition-all"
        >
          <FaEdit /> Edit Client
        </button>
      </div>

      {/* Client Info */}
      <div className="bg-white shadow-md rounded-xl mb-6 p-6">
        <h2 className="text-xl font-semibold text-gray-700 mb-4 border-b pb-2">
          Primary Contact
        </h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-gray-800 font-semibold text-lg">
              {client.first_name} {client.last_name}
            </p>
            <div className="flex items-center gap-2 mt-2 text-gray-600">
              <FaEnvelope className="text-gray-500" /> {client.email}
            </div>
            <div className="flex items-center gap-2 mt-1 text-gray-600">
              <FaPhone className="text-gray-500" />{" "}
              {client.phone_number || "—"}
            </div>
          </div>

          <div>
            <h2 className="text-lg font-medium text-gray-700 mb-1">
              Organization Info
            </h2>
            <div className="flex items-center gap-2 text-gray-600">
              <FaMapMarkerAlt className="text-gray-500" />{" "}
              {client.billing_address || "No address provided"}
            </div>
            <p className="text-gray-600 mt-1">
              {client.county_or_region || "N/A"},{" "}
              {client.state_or_province || "N/A"}
            </p>
            <p className="text-gray-600 mt-2">
              Preferred Contact:{" "}
              <span className="font-semibold">
                {client.preferred_contact_method}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Additional Contacts */}
      <div className="bg-white shadow-md rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <FaUsers className="text-blue-600" /> Additional Contacts
          </h2>
        </div>

        {client.contacts && client.contacts.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50">
                <tr>
                  {["Name", "Email", "Phone", "Address", "Can Login"].map(
                    (header) => (
                      <th
                        key={header}
                        className="px-4 py-3 font-semibold text-gray-600 border-b"
                      >
                        {header}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {client.contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    className="hover:bg-gray-50 transition border-b"
                  >
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {contact.first_name} {contact.last_name}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{contact.email}</td>
                    <td className="px-4 py-3 text-gray-700">{contact.phone}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {contact.address || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {contact.can_login ? (
                        <FaCheckCircle className="text-green-500" />
                      ) : (
                        <FaTimesCircle className="text-red-400" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 mt-2">
            No additional contacts found for this client.
          </p>
        )}
      </div>
    </div>
  );
};

export default ClientDetails;
